import { queryKeys } from '@/lib/query-keys';
import { identityClient } from '@/lib/api/ts-rest';
import type {
  Identity,
} from '@octave/contracts';
import { useQuery } from '@tanstack/react-query';
import { useCurrentOrganization } from './useOrganization';

export const useIdentity = (id: string | undefined) => {
  return useQuery<Identity, Error>({
    queryKey: queryKeys.identities.detail(id),
    queryFn: async () => {
      const res = await identityClient.getIdentity({
        params: { identityId: id! },
      });
      if (res.status !== 200) throw new Error('Failed to load identity');
      return res.body;
    },
    enabled: !!id,
  });
};

export const useSiteIdentities = (siteId: string | undefined) => {
  const { data: organization } = useCurrentOrganization();

  return useQuery<Identity[], Error>({
    queryKey: queryKeys.identities.bySite(siteId, organization?.id),
    queryFn: async () => {
      const res = await identityClient.listSiteIdentities({
        params: { organizationId: organization!.id, siteId: siteId! },
      });
      if (res.status !== 200) throw new Error('Failed to load identities');
      return res.body;
    },
    enabled: !!siteId && !!organization?.id,
  });
};

export const useOrganizationIdentities = (
  organizationId: string | undefined
) => {
  return useQuery<Identity[], Error>({
    queryKey: queryKeys.identities.byOrganization(organizationId),
    queryFn: async () => {
      const res = await identityClient.listOrganizationIdentities({
        params: { organizationId: organizationId! },
      });
      if (res.status !== 200) throw new Error('Failed to load identities');
      return res.body;
    },
    enabled: !!organizationId,
  });
};
